import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MdAdd, MdClose, MdContentCopy, MdGroupAdd, MdLink, MdStop, MdVideoCall, MdVideoLibrary } from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import VideoInviteModal from './video-invite-modal'
import VideoRecordingsModal from './video-recordings-modal'
import { usePermission } from '@/lib/hooks/use-permission'
import {
    closeVideoRoom,
    createVideoRoom,
    getVideoPlanLimits,
    issueVideoToken,
    listVideoRooms,
    type VideoParticipantRole,
    type VideoPlanLimits,
    type VideoRoom,
    type VideoRoomStatus,
} from '@/lib/api/video/video-api'

const STATUS_META: Record<VideoRoomStatus, { label: string; className: string }> = {
    IDLE: { label: 'Aguardando', className: 'badge-ghost' },
    ACTIVE: { label: 'Em andamento', className: 'badge-success' },
    CLOSED: { label: 'Encerrada', className: 'badge-neutral' },
}

const ROLE_LABELS: Record<VideoParticipantRole, string> = {
    moderator: 'Moderador',
    participant: 'Participante',
    viewer: 'Espectador (só assiste)',
}

const PAGE_SIZE = 20

function formatDateTime(value: string): string {
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

export default function VideoRoomsPage() {
    const { canCreate, canUpdate, canDelete } = usePermission()
    const [rooms, setRooms] = useState<VideoRoom[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [limits, setLimits] = useState<VideoPlanLimits | null>(null)
    const [limitsError, setLimitsError] = useState<string | null>(null)
    const [showCreate, setShowCreate] = useState(false)
    const [linkRoom, setLinkRoom] = useState<VideoRoom | null>(null)
    const [inviteRoom, setInviteRoom] = useState<VideoRoom | null>(null)
    const [recordingsRoom, setRecordingsRoom] = useState<VideoRoom | null>(null)
    const [confirmClose, setConfirmClose] = useState<VideoRoom | null>(null)
    const [closing, setClosing] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        const response = await listVideoRooms({ page, limit: PAGE_SIZE })
        if (response.success && response.result) {
            setRooms(response.result.items)
            setTotal(response.result.total)
        } else {
            setRooms([])
            setTotal(0)
        }
        setLoading(false)
    }, [page])

    useEffect(() => {
        void load()
    }, [load])

    useEffect(() => {
        void getVideoPlanLimits().then(response => {
            if (response.success && response.result) setLimits(response.result)
            else setLimitsError(response.error || 'Plano sem videoconferência')
        })
    }, [])

    const handleClose = async () => {
        if (!confirmClose) return
        setClosing(true)
        const response = await closeVideoRoom(confirmClose.id)
        setClosing(false)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível encerrar a sala')
            return
        }
        toast.success('Sala encerrada')
        setConfirmClose(null)
        void load()
    }

    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

    return (
        <div className="p-4 sm:p-6 space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold">Salas de vídeo</h1>
                    <p className="text-sm text-base-content/60 mt-0.5">
                        Salas criadas pelo painel ou pela API da organização
                    </p>
                </div>
                {canCreate('video', 'rooms') && (
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowCreate(true)}
                        disabled={!limits}
                    >
                        <MdAdd className="text-lg" />
                        Nova sala
                    </button>
                )}
            </div>

            {limits ? (
                <div className="stats stats-vertical sm:stats-horizontal shadow-sm border border-base-200 w-full">
                    <div className="stat py-3">
                        <div className="stat-title">Participantes por sala</div>
                        <div className="stat-value text-2xl">{limits.maxParticipants}</div>
                    </div>
                    <div className="stat py-3">
                        <div className="stat-title">Salas simultâneas</div>
                        <div className="stat-value text-2xl">{limits.maxConcurrentRooms}</div>
                    </div>
                    <div className="stat py-3">
                        <div className="stat-title">Duração máxima da sessão</div>
                        <div className="stat-value text-2xl">{limits.maxRoomMinutes} min</div>
                    </div>
                </div>
            ) : limitsError ? (
                <div className="alert alert-warning py-2">
                    <span className="text-sm">{limitsError}</span>
                </div>
            ) : null}

            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg text-primary" />
                </div>
            ) : !rooms.length ? (
                <div className="text-center py-16">
                    <MdVideoCall className="text-6xl mx-auto text-base-content/20 mb-3" />
                    <p className="font-medium">Nenhuma sala criada</p>
                    <p className="text-sm text-base-content/60 mt-1">
                        Crie uma sala aqui ou pela API, com uma chave em Chaves de API
                    </p>
                </div>
            ) : (
                <>
                    {/* Celular: cartões, para as ações não ficarem fora da tela. */}
                    <div className="space-y-3 sm:hidden">
                        {rooms.map(room => {
                            const status = STATUS_META[room.status]
                            return (
                                <div key={room.id} className="rounded-xl border border-base-200 bg-base-100 p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-medium truncate">{room.title || `Sala #${room.id}`}</div>
                                            <div className="text-xs text-base-content/50 font-mono break-all">
                                                {room.livekitName}
                                            </div>
                                            {room.externalId && (
                                                <div className="text-xs text-base-content/50 break-all">
                                                    ref. {room.externalId}
                                                </div>
                                            )}
                                        </div>
                                        <span className={`badge badge-sm shrink-0 ${status.className}`}>{status.label}</span>
                                    </div>
                                    <div className="mt-2 text-xs text-base-content/60">
                                        até {room.maxParticipants} participantes · {formatDateTime(room.createdAt)}
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline flex-1"
                                            onClick={() => setRecordingsRoom(room)}
                                        >
                                            <MdVideoLibrary />
                                            Gravações
                                        </button>
                                    {room.status !== 'CLOSED' && (canCreate('video', 'rooms') || canUpdate('video', 'rooms')) && (
                                        <>
                                            {canCreate('video', 'rooms') && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline flex-1"
                                                    onClick={() => setLinkRoom(room)}
                                                >
                                                    <MdLink />
                                                    Gerar link
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline flex-1"
                                                onClick={() => setInviteRoom(room)}
                                            >
                                                <MdGroupAdd />
                                                Convite
                                            </button>
                                            {canUpdate('video', 'rooms') && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline btn-error flex-1"
                                                    onClick={() => setConfirmClose(room)}
                                                >
                                                    <MdStop />
                                                    Encerrar
                                                </button>
                                            )}
                                        </>
                                    )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <div className="overflow-x-auto hidden sm:block">
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>Sala</th>
                                    <th className="w-36">Situação</th>
                                    <th className="w-32">Participantes</th>
                                    <th className="w-40">Criada em</th>
                                    <th className="w-36 text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rooms.map(room => {
                                    const status = STATUS_META[room.status]
                                    return (
                                        <tr key={room.id} className="hover">
                                            <td>
                                                <div className="font-medium">
                                                    {room.title || `Sala #${room.id}`}
                                                </div>
                                                <div className="text-xs text-base-content/50 font-mono">
                                                    {room.livekitName}
                                                    {room.externalId ? ` · ref. ${room.externalId}` : ''}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`badge badge-sm ${status.className}`}>
                                                    {status.label}
                                                </span>
                                            </td>
                                            <td className="text-base-content/70">
                                                até {room.maxParticipants}
                                            </td>
                                            <td className="text-base-content/60">
                                                {formatDateTime(room.createdAt)}
                                            </td>
                                            <td className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <button
                                                        type="button"
                                                        className="btn btn-xs btn-ghost"
                                                        onClick={() => setRecordingsRoom(room)}
                                                        title="Gravações"
                                                    >
                                                        <MdVideoLibrary />
                                                    </button>
                                                {room.status !== 'CLOSED' && (
                                                    <>
                                                        {canCreate('video', 'rooms') && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-xs btn-ghost"
                                                                onClick={() => setLinkRoom(room)}
                                                                title="Gerar link de entrada"
                                                            >
                                                                <MdLink />
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="btn btn-xs btn-ghost"
                                                            onClick={() => setInviteRoom(room)}
                                                            title="Link de convite da reunião"
                                                        >
                                                            <MdGroupAdd />
                                                        </button>
                                                        {canUpdate('video', 'rooms') && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-xs btn-ghost text-error"
                                                                onClick={() => setConfirmClose(room)}
                                                                title="Encerrar sala"
                                                            >
                                                                <MdStop />
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {pages > 1 && (
                        <div className="flex justify-center">
                            <div className="join">
                                <button
                                    type="button"
                                    className="join-item btn btn-sm"
                                    disabled={page <= 1}
                                    onClick={() => setPage(page - 1)}
                                >
                                    Anterior
                                </button>
                                <span className="join-item btn btn-sm btn-ghost pointer-events-none">
                                    {page} de {pages}
                                </span>
                                <button
                                    type="button"
                                    className="join-item btn btn-sm"
                                    disabled={page >= pages}
                                    onClick={() => setPage(page + 1)}
                                >
                                    Próxima
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {showCreate && limits && (
                <CreateRoomModal
                    maxParticipants={limits.maxParticipants ?? 2}
                    onClose={() => setShowCreate(false)}
                    onCreated={() => {
                        setShowCreate(false)
                        setPage(1)
                        void load()
                    }}
                />
            )}

            {linkRoom && <JoinLinkModal room={linkRoom} onClose={() => setLinkRoom(null)} />}
            {recordingsRoom && (
                <VideoRecordingsModal
                    room={recordingsRoom}
                    canEdit={canUpdate('video', 'rooms')}
                    canDelete={canDelete('video', 'rooms')}
                    onClose={() => setRecordingsRoom(null)}
                />
            )}
            {inviteRoom && (
                <VideoInviteModal
                    room={inviteRoom}
                    editable={canUpdate('video', 'rooms')}
                    onClose={() => setInviteRoom(null)}
                />
            )}

            <ConfirmModal
                isOpen={Boolean(confirmClose)}
                onClose={() => setConfirmClose(null)}
                onConfirm={handleClose}
                loading={closing}
                title="Encerrar sala"
                message="Todos os participantes saem agora e a sala não aceita mais entradas. Esta ação não pode ser desfeita."
                confirmButtonText="Encerrar"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}

function CreateRoomModal({
    maxParticipants,
    onClose,
    onCreated,
}: {
    maxParticipants: number
    onClose: () => void
    onCreated: () => void
}) {
    const [title, setTitle] = useState('')
    const [externalId, setExternalId] = useState('')
    const [participants, setParticipants] = useState(String(maxParticipants))
    const [autoRecord, setAutoRecord] = useState(false)
    const [saving, setSaving] = useState(false)

    const handleSave = async () => {
        const value = Number(participants)
        if (!Number.isInteger(value) || value < 2 || value > maxParticipants) {
            toast.error(`Informe de 2 a ${maxParticipants} participantes`)
            return
        }
        setSaving(true)
        const response = await createVideoRoom({
            title: title.trim() || undefined,
            externalId: externalId.trim() || undefined,
            maxParticipants: value,
            autoRecord,
        })
        setSaving(false)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível criar a sala')
            return
        }
        toast.success('Sala criada')
        onCreated()
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-lg">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <MdVideoCall className="text-xl text-primary" />
                        Nova sala
                    </h3>
                    <button type="button" className="btn btn-sm btn-ghost btn-circle" onClick={onClose}>
                        <MdClose className="text-xl" />
                    </button>
                </div>

                <div className="space-y-4">
                    <div className="form-control w-full">
                        <label className="label pb-1.5" htmlFor="video-room-title">
                            <span className="flex w-full items-baseline justify-between gap-3">
                                <span className="label-text font-medium">Título</span>
                                <span className="label-text-alt text-base-content/50">Opcional</span>
                            </span>
                        </label>
                        <input
                            id="video-room-title"
                            type="text"
                            className="input input-bordered w-full"
                            value={title}
                            maxLength={200}
                            placeholder="Ex.: Reunião semanal"
                            onChange={event => setTitle(event.target.value)}
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label pb-1.5" htmlFor="video-room-external">
                                <span className="flex w-full items-baseline justify-between gap-3">
                                    <span className="label-text font-medium">Referência externa</span>
                                    <span className="label-text-alt text-base-content/50">Opcional</span>
                                </span>
                            </label>
                            <input
                                id="video-room-external"
                                type="text"
                                className="input input-bordered w-full"
                                value={externalId}
                                maxLength={200}
                                placeholder="Id no seu sistema"
                                onChange={event => setExternalId(event.target.value)}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label pb-1.5" htmlFor="video-room-max">
                                <span className="flex w-full items-baseline justify-between gap-3">
                                    <span className="label-text font-medium">Participantes</span>
                                    <span className="label-text-alt text-base-content/50">
                                        até {maxParticipants}
                                    </span>
                                </span>
                            </label>
                            <input
                                id="video-room-max"
                                type="number"
                                min={2}
                                max={maxParticipants}
                                className="input input-bordered w-full"
                                value={participants}
                                onChange={event => setParticipants(event.target.value)}
                            />
                        </div>
                    </div>

                    <label className="label cursor-pointer justify-start items-start gap-3 p-0 whitespace-normal">
                        <input
                            type="checkbox"
                            className="toggle toggle-primary mt-0.5"
                            checked={autoRecord}
                            onChange={event => setAutoRecord(event.target.checked)}
                        />
                        <span>
                            <span className="label-text font-medium">Gravar automaticamente</span>
                            <span className="block text-xs text-base-content/60 mt-0.5">
                                Cada reunião desta sala é gravada do início ao fim. Todos na sala são avisados. Exige
                                plano com gravação.
                            </span>
                        </span>
                    </label>
                </div>

                <div className="modal-action">
                    <button type="button" className="btn btn-ghost" onClick={onClose}>
                        Cancelar
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                        {saving && <span className="loading loading-spinner loading-sm" />}
                        Criar sala
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}

function JoinLinkModal({ room, onClose }: { room: VideoRoom; onClose: () => void }) {
    const [name, setName] = useState('')
    const [role, setRole] = useState<VideoParticipantRole>('moderator')
    const [link, setLink] = useState<string | null>(null)
    const [creating, setCreating] = useState(false)

    const handleCreate = async () => {
        if (!name.trim()) {
            toast.error('Informe o nome de quem vai entrar')
            return
        }
        setCreating(true)
        const response = await issueVideoToken(room.id, { name: name.trim(), role })
        setCreating(false)
        if (!response.success || !response.result) {
            toast.error(response.error || 'Não foi possível gerar o link')
            return
        }
        if (!response.result.joinUrl) {
            toast.error('O endereço da sala pronta não está configurado na plataforma')
            return
        }
        setLink(response.result.joinUrl)
    }

    const handleCopy = async () => {
        if (!link) return
        await navigator.clipboard.writeText(link)
        toast.success('Link copiado')
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-lg">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <MdLink className="text-xl text-primary" />
                        Link de entrada
                    </h3>
                    <button type="button" className="btn btn-sm btn-ghost btn-circle" onClick={onClose}>
                        <MdClose className="text-xl" />
                    </button>
                </div>
                <p className="text-sm text-base-content/70 mb-5 truncate">
                    {room.title || `Sala #${room.id}`}
                </p>

                {link ? (
                    <div className="space-y-4">
                        <div className="form-control w-full">
                            <label className="label pb-1.5" htmlFor="video-join-link">
                                <span className="flex w-full items-baseline justify-between gap-3">
                                    <span className="label-text font-medium">Link pessoal de {name}</span>
                                </span>
                            </label>
                            <div className="join w-full">
                                <input
                                    id="video-join-link"
                                    type="text"
                                    className="input input-bordered join-item w-full font-mono text-xs"
                                    value={link}
                                    readOnly
                                />
                                <button type="button" className="btn btn-primary join-item" onClick={handleCopy}>
                                    <MdContentCopy />
                                    Copiar
                                </button>
                            </div>
                        </div>
                        <div className="alert alert-info py-2">
                            <span className="text-sm">
                                O link é pessoal e expira em 2 horas. Para cada pessoa, gere um link próprio.
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label pb-1.5" htmlFor="video-join-name">
                                <span className="flex w-full items-baseline justify-between gap-3">
                                    <span className="label-text font-medium">Nome</span>
                                </span>
                            </label>
                            <input
                                id="video-join-name"
                                type="text"
                                className="input input-bordered w-full"
                                value={name}
                                maxLength={120}
                                placeholder="Como aparece na sala"
                                onChange={event => setName(event.target.value)}
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label pb-1.5" htmlFor="video-join-role">
                                <span className="flex w-full items-baseline justify-between gap-3">
                                    <span className="label-text font-medium">Papel</span>
                                </span>
                            </label>
                            <select
                                id="video-join-role"
                                className="select select-bordered w-full"
                                value={role}
                                onChange={event => setRole(event.target.value as VideoParticipantRole)}
                            >
                                {(Object.keys(ROLE_LABELS) as VideoParticipantRole[]).map(key => (
                                    <option key={key} value={key}>
                                        {ROLE_LABELS[key]}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                <div className="modal-action">
                    <button type="button" className="btn btn-ghost" onClick={onClose}>
                        {link ? 'Fechar' : 'Cancelar'}
                    </button>
                    {!link && (
                        <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={creating}>
                            {creating && <span className="loading loading-spinner loading-sm" />}
                            Gerar link
                        </button>
                    )}
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}
